import { rest } from "msw";
import type { RestHandler } from "msw";
import config from "../../config";
import {
  apiVersion,
  UserStatus,
  UserRoles,
} from "../../shared/constants/common.constant";
import type { ApiUsersRolesResponse, ApiUserRole } from "./user-roles-api";
import { getTeamId } from "./user-roles-test-data";

export const filterUsersByQueryParams = (
  users: ApiUserRole[],
  searchParams: URLSearchParams
): ApiUserRole[] => {
  const status = searchParams.get("status");
  const searchTerm =
    searchParams.get("searchTerm") ?? searchParams.get("searchterm");

  let filtered = users;

  if (status) {
    filtered = filtered.filter(
      (user) => user.status.toLowerCase() === status.toLowerCase()
    );
  }

  if (searchTerm?.trim()) {
    const normalizedSearch = searchTerm.toLowerCase().trim();
    filtered = filtered.filter((user) => {
      const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
      return (
        fullName.includes(normalizedSearch) ||
        user.email.toLowerCase().includes(normalizedSearch) ||
        user.userId.toString().includes(normalizedSearch)
      );
    });
  }

  return filtered;
};

const generateAdditionalUsers = (
  startId: number,
  count: number
): ApiUserRole[] => {
  const users: ApiUserRole[] = [];
  const roles = [
    UserRoles.Admin,
    UserRoles.Manager,
    UserRoles.Analyst,
    UserRoles.Viewer,
  ];
  const teamNames = ["Default", "Team Name", "Team Name 2"];
  const statuses: UserStatus[] = [UserStatus.Active, UserStatus.Inactive];

  for (let i = 0; i < count; i++) {
    const userId = startId + i;
    const role = roles[i % roles.length];
    const teamName = teamNames[i % teamNames.length];
    const status = statuses[i % 2];
    const firstName = `User`;
    const lastName = `${userId}`;

    users.push({
      userId,
      firstName,
      lastName,
      email: `user${userId}@123test.com`,
      status,
      teamRoles: [
        {
          roleId: (i % roles.length) + 1,
          roleName: role,
          teamId: getTeamId(teamName, role),
          teamName,
        },
      ],
    });
  }

  return users;
};

export const mockGetUsersRoles = (
  usersData?: ApiUsersRolesResponse,
  apiFailed = false
): RestHandler => {
  const usersRolesUrl = `${config.KYCP_AUTH_API_URL}/${apiVersion}/users/roles`;

  const firstTenUsers: ApiUserRole[] = [
    {
      userId: 89789097,
      firstName: "John",
      lastName: "Smith",
      email: "john.smith@123test.com",
      status: UserStatus.Active,
      teamRoles: [
        {
          roleId: 1,
          roleName: UserRoles.Admin,
          teamId: getTeamId("Default", UserRoles.Admin),
          teamName: "Default",
        },
      ],
    },
    {
      userId: 78909090,
      firstName: "Jane",
      lastName: "Doe",
      email: "jane.doe@123test.com",
      status: UserStatus.Active,
      teamRoles: [
        {
          roleId: 1,
          roleName: UserRoles.Admin,
          teamId: getTeamId("Default", UserRoles.Admin),
          teamName: "Default",
        },
      ],
    },
    {
      userId: 78908790,
      firstName: "Lorem",
      lastName: "Ipsum",
      email: "lorem.ipsum@123test.com",
      status: UserStatus.Active,
      teamRoles: [
        {
          roleId: 2,
          roleName: UserRoles.Manager,
          teamId: getTeamId("Default", UserRoles.Manager),
          teamName: "Default",
        },
      ],
    },
    {
      userId: 12909090,
      firstName: "User",
      lastName: "1",
      email: "lorem.ipsum@123test.com",
      status: UserStatus.Active,
      teamRoles: [
        {
          roleId: 2,
          roleName: UserRoles.Manager,
          teamId: getTeamId("Default", UserRoles.Manager),
          teamName: "Default",
        },
      ],
    },
    {
      userId: 12945090,
      firstName: "User",
      lastName: "2",
      email: "lorem.ipsum@123test.com",
      status: UserStatus.Active,
      teamRoles: [
        {
          roleId: 2,
          roleName: UserRoles.Manager,
          teamId: getTeamId("Default", UserRoles.Manager),
          teamName: "Default",
        },
      ],
    },
    {
      userId: 12745090,
      firstName: "User",
      lastName: "3",
      email: "lorem.ipsum@123test.com",
      status: UserStatus.Inactive,
      teamRoles: [
        {
          roleId: 2,
          roleName: UserRoles.Manager,
          teamId: getTeamId("Default", UserRoles.Manager),
          teamName: "Default",
        },
      ],
    },
    {
      userId: 12745091,
      firstName: "User",
      lastName: "4",
      email: "lorem.ipsum@123test.com",
      status: UserStatus.Active,
      teamRoles: [
        {
          roleId: 3,
          roleName: UserRoles.Analyst,
          teamId: getTeamId("Team Name", UserRoles.Analyst),
          teamName: "Team Name",
        },
      ],
    },
    {
      userId: 12745092,
      firstName: "User",
      lastName: "5",
      email: "lorem.ipsum@123test.com",
      status: UserStatus.Active,
      teamRoles: [
        {
          roleId: 1,
          roleName: UserRoles.Admin,
          teamId: getTeamId("Default", UserRoles.Admin),
          teamName: "Default",
        },
      ],
    },
    {
      userId: 12745093,
      firstName: "User",
      lastName: "6",
      email: "lorem.ipsum@123test.com",
      status: UserStatus.Active,
      teamRoles: [
        {
          roleId: 1,
          roleName: UserRoles.Admin,
          teamId: getTeamId("Team Name 2", UserRoles.Admin),
          teamName: "Team Name 2",
        },
      ],
    },
    {
      userId: 12745094,
      firstName: "User",
      lastName: "7",
      email: "lorem.ipsum@123test.com",
      status: UserStatus.Inactive,
      teamRoles: [
        {
          roleId: 4,
          roleName: UserRoles.Viewer,
          teamId: getTeamId("Team Name 2", UserRoles.Viewer),
          teamName: "Team Name 2",
        },
      ],
    },
  ];

  const additionalUsers = generateAdditionalUsers(12745095, 95);
  const allUsers = [...firstTenUsers, ...additionalUsers];

  const defaultData: ApiUsersRolesResponse = {
    items: allUsers,
    count: allUsers.length,
  };

  return rest.get(usersRolesUrl, async (req, res, ctx) => {
    if (apiFailed) {
      return res(ctx.status(500));
    }

    const sourceData = usersData ?? defaultData;
    const filteredItems = filterUsersByQueryParams(
      sourceData.items,
      new URL(req.url).searchParams
    );

    return res(
      ctx.status(200),
      ctx.json({
        items: filteredItems,
        count: filteredItems.length,
      })
    );
  });
};

export const mockPutUserRole = (
  options?: {
    fail?: boolean;
    errorDetail?: string;
    errorMessage?: string;
    emptyErrorPayload?: boolean;
  }
): RestHandler => {
  const usersRolesUrl = `${config.KYCP_AUTH_API_URL}/${apiVersion}/users/roles`;
  return rest.put(usersRolesUrl, async (_, res, ctx) => {
    if (options?.fail) {
      if (options.errorDetail) {
        return res(
          ctx.status(400),
          ctx.json({
            type: "BadRequest",
            title: "Last Admin Removal",
            status: 400,
            detail: options.errorDetail,
          })
        );
      }
      if (options.errorMessage) {
        return res(
          ctx.status(400),
          ctx.json({
            message: options.errorMessage,
          })
        );
      }
      if (options.emptyErrorPayload) {
        return res(ctx.status(400), ctx.json({}));
      }
      return res(ctx.status(500));
    }
    return res(ctx.status(204));
  });
};

export const mockDownloadUsersRolesCsv = (
  apiFailed = false
): RestHandler => {
  const downloadUrl = `${config.KYCP_AUTH_API_URL}/${apiVersion}/users/roles/download`;

  return rest.get(downloadUrl, async (_, res, ctx) => {
    if (apiFailed) {
      return res(ctx.status(500));
    }

    return res(
      ctx.status(200),
      ctx.json({
        downloadUrl: "https://example.com/users-roles.xlsx",
        fileName: "users-roles.xlsx",
        expiresAt: "2026-06-12T08:37:16.366Z",
      })
    );
  });
};
